import type { Report } from '#lib/types';
import { t } from '#lib/i18n/state.svelte';
import type { JournalCheck, JournalEvaluation, JournalRule } from './types';

const HEADING =
	/^(abstract|öz|ozet|özet|keywords|anahtar\s*kelime(?:ler)?|introduction|giriş|background|methods?|yöntem(?:ler)?|results?|bulgular|discussion|tartışma|conclusion|sonuç|references|kaynakça)\b/i;

export function abstractText(text: string): string {
	const lines = text.split('\n');
	const start = lines.findIndex((l) => /^(abstract|öz|ozet|özet)\b/i.test(l.trim()));
	if (start === -1) return '';
	const out: string[] = [];
	for (let i = start + 1; i < lines.length; i++) {
		const line = lines[i].trim();
		if (!line) continue;
		if (HEADING.test(line) && !/^abstract\b/i.test(line)) break;
		out.push(line);
		if (out.join(' ').split(/\s+/).length > 600) break;
	}
	return out.join(' ');
}

function countWords(text: string): number {
	return text.trim() ? text.trim().split(/\s+/).length : 0;
}

function detectReferenceStyle(report: Report): 'numbered' | 'author-year' | 'unknown' {
	const refs = report.citations.references.map((r) => r.raw).join('\n');
	if (!refs.trim()) return 'unknown';
	const numbered = (refs.match(/^\s*(?:\[\d+\]|\d+\.)\s+/gm) ?? []).length;
	const authorYear = (refs.match(/\(\d{4}[a-z]?\)/g) ?? []).length;
	if (numbered >= 2 && numbered >= authorYear) return 'numbered';
	if (authorYear >= 2) return 'author-year';
	return 'unknown';
}

function hasStatement(report: Report, text: string, statement: string): boolean {
	if (statement === 'author_contributions') {
		return /author\s+contributions?|CRediT|yazar\s+katk/i.test(text);
	}
	if (statement === 'registration') {
		return /NCT\d{4,}|ISRCTN\d+|trial registration|registration number|kayıt numarası|PROSPERO/i.test(
			text
		);
	}
	return report.access.checks.find((c) => c.key === statement)?.status === 'ok';
}

function runCheck(check: JournalCheck, report: Report, text: string): { passed: boolean; message: string } {
	const params: Record<string, string | number> = { ...(check.params as Record<string, string | number>) };

	if (check.type === 'word_limit') {
		const scope = String(check.params.scope ?? 'abstract');
		const max = Number(check.params.max ?? 300);
		const actual = countWords(scope === 'full' ? text : abstractText(text));
		params.max = max;
		params.actual = actual;
		return { passed: actual <= max, message: t(check.messageKey, params) };
	}

	if (check.type === 'required_section') {
		const section = String(check.params.section ?? '');
		params.sectionLabel = t(`struct.section.${section}`);
		const passed = report.structure.sections.some((s) => s.key === section);
		return { passed, message: t(check.messageKey, params) };
	}

	if (check.type === 'required_statement') {
		const statement = String(check.params.statement ?? 'data');
		params.statementLabel = t(`journal.statement.${statement}`);
		return { passed: hasStatement(report, text, statement), message: t(check.messageKey, params) };
	}

	if (check.type === 'reference_style') {
		const expected = String(check.params.style ?? 'vancouver');
		const detected = detectReferenceStyle(report);
		params.detected = t(`journal.ref.${detected}`);
		params.style = t(`journal.style.${expected}`);
		const passed =
			detected === 'unknown' ||
			(expected === 'vancouver' || expected === 'ieee' ? detected === 'numbered' : detected === 'author-year');
		return { passed, message: t(check.messageKey, params) };
	}

	const rule = String(check.params.rule ?? '');
	params.ruleLabel = t(`journal.rule.${rule}`);
	let passed = true;
	if (rule === 'structured_abstract') {
		const abstract = abstractText(text).toLowerCase();
		const labels = ['background', 'objective', 'methods', 'results', 'conclusion'].filter((l) =>
			new RegExp(`(?:^|\\n)\\s*${l}\\s*[:：]`, 'i').test(abstract)
		).length;
		passed = labels >= 3;
	} else if (rule === 'imrad') {
		const keys = new Set(report.structure.sections.map((s) => s.key));
		passed = keys.has('method') && keys.has('results') && keys.has('discussion');
	} else if (rule === 'keywords') {
		passed = report.structure.sections.some((s) => s.key === 'keywords');
	}
	return { passed, message: t(check.messageKey, params) };
}

export function evaluateJournal(journal: JournalRule, report: Report, text: string): JournalEvaluation {
	const results = journal.checks.map((check) => {
		const { passed, message } = runCheck(check, report, text);
		return { code: check.code, passed, severity: check.severity, message };
	});
	return {
		journalId: journal.id,
		name: journal.name,
		publisher: journal.publisher,
		passed: results.filter((r) => r.passed).length,
		total: results.length,
		results
	};
}
